/**
 * QPS Management — the APP Plus Gap-Management-equivalent page, built to the
 * BINDING conformance checklist in docs/reference/app-plus/UI_ALIGNMENT.md §8
 * (locked with the owner 2026-07-14 PM via chat wireframes, D9 process).
 *
 * Kept as-built (owner-approved): score strip + Path to QPS card.
 * Statuses: COMPUTED / PROJECTED / AWAITING with tooltips (D10).
 * Entry surface: the measure sheet (AppMeasureSheet) — NOT expanded rows (D4).
 * 321 expands to its SSM strata, config-driven (D11).
 * View Details / Chart Abstraction: committed, disabled until the ACO-upload
 * wiring phase (D12).
 */
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpDown, BarChart3, ChevronDown, ChevronRight, CircleHelp, Columns,
  Download, MoreHorizontal, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import DecileBenchmarkBar, { type DecileRange } from '@/components/gap-management/shared/BenchmarkChart';
import TableViewsManager, {
  type ColumnDef, type ColumnPreset as ViewPreset, type ColumnSettingsValue,
} from '@/components/gap-management/shared/TableViewsManager';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartAbstractionActionMenuItem, ChartAbstractionExportModal } from '@/components/gap-management/chart-abstraction';
import { useChartAbstraction } from '@/contexts/ChartAbstractionContext';
import { apiClient } from '@/lib/api-client';
import userStorage from '@/lib/userStorage';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import type { AppMeasure, AppBenchmarkCell } from '@/lib/app-plus-data-service';
import { logicForYear } from '@/lib/app-plus/years/registry';
import { parseDecileRange } from '@/lib/scoring-core';
import type { AppQualityScore, MeasureScoreResult, CahpsScoreResult, PathwayResult } from '@/lib/app-plus-scoring';
import {
  useAppPlusScoring, CLINICAL_IDS, type StoredSubmission, type SimulateScenario,
} from './lib/useAppPlusScoring';
import { AcoHeader, NoAcoState, YearUnsupportedState } from './components/AcoHeader';
import { AcoActionsCard } from './components/AcoActionsCard';
import { AppMeasureSheet } from './components/AppMeasureSheet';
import { NextDecileChip, CAHPS_PROJECTION_CAVEAT } from './lib/cahpsInsights';
import appStrata2026 from '@/data/2026-app-data/app_2026_strata_measures.json';

const CLINICAL_TYPES = ['eCQM', 'MIPS CQM', 'Medicare CQM'] as const;
type SortKey = 'id' | 'points' | 'rate' | 'dc' | 'status';
interface Sorter { key: SortKey; dir: 'asc' | 'desc' }
interface Filters {
  collectionTypes: string[];   // active pills
  inverse: boolean;
  outcome: boolean;
  belowCaseMin: boolean;
  awaiting: boolean;
}
const NO_FILTERS: Filters = { collectionTypes: [], inverse: false, outcome: false, belowCaseMin: false, awaiting: false };

// ===== Column model (owner 2026-07-20: full Gap-Management views parity) =====
// The table renders from an ordered, per-column-visible settings object and the
// views are managed by the SAME TableViewsManager component MIPS Summary uses.
type QpsColKey =
  | 'id' | 'title' | 'flags' | 'ct' | 'cmsAvg'
  | 'ip' | 'ex' | 'den' | 'met' | 'exc' | 'not'
  | 'dc' | 'rate' | 'points' | 'goal' | 'actions';

const QPS_COLUMNS: ColumnDef<QpsColKey>[] = [
  { key: 'id', label: 'Measure ID', lock: true },
  { key: 'title', label: 'Title', lock: true },
  { key: 'flags', label: 'Flags' },
  { key: 'ct', label: 'Collection Type' },
  { key: 'cmsAvg', label: 'CMS Avg' },
  { key: 'ip', label: 'Eligible Pop' },
  { key: 'ex', label: 'Exclusions' },
  { key: 'den', label: 'Denominator' },
  { key: 'met', label: 'Met' },
  { key: 'exc', label: 'Exceptions' },
  { key: 'not', label: 'Not Met' },
  { key: 'dc', label: 'Complete Rate' },
  { key: 'rate', label: 'Performance Rate' },
  { key: 'points', label: 'Est. Points' },
  { key: 'goal', label: 'Goal' },
  { key: 'actions', label: 'Actions', lock: true },
];
const ALL_KEYS = QPS_COLUMNS.map(c => c.key);

const settingsFor = (visible: QpsColKey[], tooltips = true): ColumnSettingsValue<QpsColKey> => ({
  order: [...ALL_KEYS],
  visibility: Object.fromEntries(ALL_KEYS.map(k => [k, visible.includes(k)])) as Record<QpsColKey, boolean>,
  tooltips,
});

// System presets MIRROR Gap Management's exactly (owner 2026-07-20), plus the
// QPS-specific always-on columns (Collection Type = the what-if dimension,
// Actions). Like MIPS: Counts has NO Eligible Pop/Exclusions and KEEPS the
// benchmark graph; even Full View leaves ip/ex to Manage Views. Goal is
// opt-in (mostly blank until real data scores — owner 2026-07-20). Summary
// drops the Flags COLUMN (owner 2026-07-20 PM): when the column is hidden,
// the same insights render as a muted sub-line under the Title.
const SYSTEM_PRESETS: ViewPreset<QpsColKey>[] = [
  { id: 'summary', name: 'Summary (Policy)', builtin: true,
    settings: settingsFor(['id', 'title', 'ct', 'cmsAvg', 'dc', 'rate', 'points', 'actions']) },
  { id: 'counts', name: 'Counts (Operational)', builtin: true,
    settings: settingsFor(['id', 'title', 'ct', 'den', 'met', 'exc', 'not', 'dc', 'rate', 'points', 'actions']) },
  // Flags column is opt-in everywhere (owner 2026-07-20 PM) — the title
  // sub-line carries the flags in every system preset.
  { id: 'full', name: 'Full View', builtin: true,
    settings: settingsFor(['id', 'title', 'ct', 'cmsAvg', 'den', 'met', 'exc', 'not', 'dc', 'rate', 'points', 'actions']) },
];
const DEFAULT_SETTINGS = SYSTEM_PRESETS[0].settings;

/** Persisted user views (v2 — the v1 preset-snapshot shape is obsolete and ignored). */
const VIEWS_KEY = 'app:qps:views:v2';
interface PersistedViews { user: ViewPreset<QpsColKey>[]; activeId?: string }
const loadViews = (): PersistedViews => {
  try {
    const p = JSON.parse(userStorage.getItem(VIEWS_KEY) || '{}');
    return { user: Array.isArray(p.user) ? p.user : [], activeId: typeof p.activeId === 'string' ? p.activeId : undefined };
  } catch { return { user: [] }; }
};

// D11: strata declarations are config-driven per year (the MIPS pattern).
const STRATA_BY_YEAR: Record<number, Record<string, { strataLabel: string }>> = {
  2026: appStrata2026 as unknown as Record<string, { strataLabel: string }>,
};

// ---------- helpers ----------

function decilesForCell(cell: AppBenchmarkCell): DecileRange[] {
  if (!cell.row) return [];
  const out: DecileRange[] = [];
  for (let i = 1; i <= 10; i++) {
    const { min, max } = parseDecileRange(cell.row[`decile_${i}` as 'decile_1']);
    if (min == null || max == null) continue;
    out.push({ from: min, to: max });
  }
  return out;
}

function cmsAvgFor(cell: AppBenchmarkCell): number | null {
  const raw = (cell.row as unknown as Record<string, string> | null)?.cmsAverage;
  if (!raw || raw === '--') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

// (The standalone status badge is GONE — owner 2026-07-20: attention states
// render as tinted Flags pills; the settled/default state needs no label.)

/**
 * Goal flag (owner 2026-07-20): does this measure currently clear its CMS
 * alternative-standard leg — outcome measures against the 10th-percentile bar,
 * everything else against the 40th (§425.512; derived decile rule §6).
 */
function GoalFlag({ result, isOutcome, tenthMin, fortiethMin }: {
  result: MeasureScoreResult; isOutcome: boolean; tenthMin: number; fortiethMin: number;
}) {
  // Blank when there's nothing to say (owner 2026-07-20 — a dash is noise).
  if (result.state !== 'scored') return null;
  const min = isOutcome ? tenthMin : fortiethMin;
  const met = result.points >= min;
  const bar = isOutcome ? '10th percentile (outcome leg)' : '40th percentile';
  const tip = `CMS quality goal for this measure: the ${bar} — needs ≥ ${min} points; currently ${result.points.toFixed(1)}.`;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-default">
            {met
              ? <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 whitespace-nowrap">✓ Met</Badge>
              : <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50 whitespace-nowrap">Below</Badge>}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Cell tooltip wrapper gated by the toolbar Tooltips switch (MIPS behavior). */
function CellTip({ on, tip, children }: { on: boolean; tip: React.ReactNode; children: React.ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild><span className="inline-flex cursor-default">{children}</span></TooltipTrigger>
        <TooltipContent className="max-w-xs">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** §6.4 metadata pill — tinted tones for measure-state flags (owner 2026-07-20). */
function MetaPill({ children, title, tone = 'slate' }: {
  children: React.ReactNode; title?: string; tone?: 'slate' | 'amber' | 'blue';
}) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70',
    amber: 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100/70',
    blue: 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100/70',
  } as const;
  return (
    <Badge variant="secondary" title={title}
      className={cn('rounded-full whitespace-nowrap px-2 py-0.5 text-[11px] font-medium', tones[tone])}>
      {children}
    </Badge>
  );
}

function fixedTypeLabel(m: AppMeasure): string {
  if (m.measureId === '321') return 'CAHPS';
  if (m.measureId === '479' || m.measureId === '484') return 'Admin Claims';
  return m.collectionTypes[0];
}

/**
 * Pathway 3's blocking list (owner 2026-07-20: "incomplete" must show its
 * work) — which measures fail the reported-with-data-completeness condition.
 */
function pathway3Blockers(measures: MeasureScoreResult[], dcThreshold: number): string[] {
  const out: string[] = [];
  for (const m of measures) {
    if (m.state === 'unreported') out.push(`${m.measureId} not reported`);
    else if (m.state === 'dc_miss' ||
      (m.dataCompletenessPct != null && m.dataCompletenessPct < dcThreshold)) {
      out.push(`${m.measureId} DC ${m.dataCompletenessPct?.toFixed(0) ?? 0}%`);
    }
  }
  return out;
}

/** Plain-language pathway sentence (D3 — owner-approved as built). */
function pathwaySentence(id: string, p: PathwayResult, qpsThreshold: number, composite: number, p3Blockers?: string[]): string {
  switch (id) {
    case 'p1':
      return p.status === 'met'
        ? `Pathway 1 — score at least ${qpsThreshold}: met (you're at ${composite.toFixed(2)}).`
        : `Pathway 1 — score at least ${qpsThreshold}: not met (you're at ${composite.toFixed(2)}).`;
    case 'p2':
      if (p.status === 'disqualified') return `Pathway 2 — the eCQM/MIPS CQM reporting incentive: unavailable (${p.reason.replace(/\(§6.*\)/, '').trim()}).`;
      if (p.status === 'met') return 'Pathway 2 — the eCQM/MIPS CQM reporting incentive: met on secured measures.';
      if (p.status === 'projected') return 'Pathway 2 — the reporting incentive: on track, but a leg rides on an estimate — not secured yet.';
      if (p.status === 'unknowable') return 'Pathway 2 — the reporting incentive: can\u2019t be evaluated until a missing benchmark posts.';
      return 'Pathway 2 — the eCQM/MIPS CQM reporting incentive: percentile legs not met.';
    case 'p3': {
      if (p.status === 'met') return 'Pathway 3 — first-year ACO: met (reported the set with data completeness + CAHPS).';
      if (p.status === 'disqualified') return 'Pathway 3 — first-year ACO: does not apply to this ACO.';
      // Show the work (owner 2026-07-20): name the blocking measures.
      const blockers = p3Blockers ?? [];
      const shown = blockers.slice(0, 4).join(' · ');
      const more = blockers.length > 4 ? ` +${blockers.length - 4} more` : '';
      return blockers.length > 0
        ? `Pathway 3 — first-year ACO: blocked by ${shown}${more} (every measure must be reported with ≥75% completeness).`
        : 'Pathway 3 — first-year ACO: reporting/data completeness incomplete.';
    }
    default:
      if (p.reason.startsWith('both-fail')) {
        return 'Alternative standard — unavailable: nothing was reported (no clinical measures, no CAHPS) — fails both standards.';
      }
      if (p.reason.startsWith('gray zone')) {
        return 'Alternative standard — provisionally met on CMS-computed data only (no clinical submissions): flagged, not secured.';
      }
      return p.status === 'met' || p.status === 'projected'
        ? `Alternative standard — one outcome measure at the 10th percentile: ${p.status === 'met' ? 'met' : 'met on a projected estimate'} (unlocks a scaled sharing rate even if the QPS is missed).`
        : 'Alternative standard — one outcome measure at the 10th percentile: not met.';
  }
}

// ---------- page ----------

export default function QpsManagement() {
  const navigate = useNavigate();
  const { selectedAco, loading: acoLoading } = useAco();
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const { toast } = useToast();
  const scoring = useAppPlusScoring(selectedAco?.id ?? null, selectedAco?.shadow_practice_id ?? null);

  // Pending drafts (ephemeral until Confirm) + UI state
  const [draft, setDraft] = useState<Record<string, Partial<StoredSubmission>>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [stripOpen, setStripOpen] = useState(false);
  const [pathOpen, setPathOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Simulate Scoring Scenario (toolbar): 'off' | 'delta:x' | 'rule:x' | 'ct:x'
  const [simValue, setSimValue] = useState('off');
  // Sort & Filter panel
  const [sortOpen, setSortOpen] = useState(false);
  const [sorters, setSorters] = useState<Sorter[]>([]);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  // Column views — full Gap-Management parity via TableViewsManager (owner 2026-07-20)
  const [userViews, setUserViews] = useState<ViewPreset<QpsColKey>[]>(() => loadViews().user);
  const [activePresetId, setActivePresetId] = useState<string>(() => loadViews().activeId ?? 'summary');
  const [colSettings, setColSettings] = useState<ColumnSettingsValue<QpsColKey>>(() => {
    const p = loadViews();
    const active = [...SYSTEM_PRESETS, ...p.user].find(v => v.id === (p.activeId ?? 'summary'));
    return active?.settings ?? DEFAULT_SETTINGS;
  });
  const [manageViewsOpen, setManageViewsOpen] = useState(false);
  const [abstractionModalOpen, setAbstractionModalOpen] = useState(false);
  const [exportingPatients, setExportingPatients] = useState(false);
  const { pendingCount } = useChartAbstraction();

  const presets = useMemo(() => [...SYSTEM_PRESETS, ...userViews], [userViews]);
  const tooltips = colSettings.tooltips;
  const setTooltips = (v: boolean) => setColSettings(prev => ({ ...prev, tooltips: v }));

  const persistViews = (user: ViewPreset<QpsColKey>[], activeId: string) => {
    try { userStorage.setItem(VIEWS_KEY, JSON.stringify({ user, activeId } satisfies PersistedViews)); } catch { /* quota */ }
  };
  const applyPreset = (id: string) => {
    const p = [...SYSTEM_PRESETS, ...userViews].find(v => v.id === id);
    if (!p) return;
    setActivePresetId(id);
    setColSettings(p.settings);
    persistViews(userViews, id);
  };

  // D13: all business calculations resolve through the year's logic module.
  const logic = useMemo(() => {
    try { return logicForYear(programYear); } catch { return null; }
  }, [programYear]);
  const dirty = Object.keys(draft).length > 0;
  const applied = scoring.applied;

  const measureSet = useMemo(() => {
    try { return logic?.getMeasureSet(programYear) ?? null; } catch { return null; }
  }, [logic, programYear]);
  const constants = useMemo(() => {
    try { return logic?.getConstants(programYear) ?? null; } catch { return null; }
  }, [logic, programYear]);

  // Decode the toolbar scenario
  const scenario: SimulateScenario | undefined = useMemo(() => {
    if (simValue.startsWith('delta:')) return { deltaPct: Number(simValue.split(':')[1]) };
    if (simValue === 'rule:no75') return { waiveDataCompleteness: true };
    if (simValue === 'rule:noden') return { waiveCaseMinimum: true };
    if (simValue === 'rule:both') return { waiveDataCompleteness: true, waiveCaseMinimum: true };
    return undefined;
  }, [simValue]);
  const ctOverride = simValue.startsWith('ct:') ? simValue.slice(3) : null;
  const simActive = simValue !== 'off';

  // What the page displays: drafts + scenario merged over persisted rows.
  const displayScore: AppQualityScore | null = useMemo(() => {
    const overrides: Record<string, Partial<StoredSubmission>> = { ...draft };
    if (ctOverride) {
      for (const id of (logic?.clinicalMeasureIds ?? CLINICAL_IDS)) {
        overrides[id] = { ...overrides[id], collection_type: ctOverride };
      }
    }
    if (Object.keys(overrides).length === 0 && !scenario) return applied;
    return scoring.simulate(overrides, scenario);
  }, [draft, ctOverride, scenario, scoring, applied]);

  if (!scoring.yearSupported) return <YearUnsupportedState year={programYear} />;
  if (acoLoading || scoring.loading) {
    // Shell-first (owner 2026-07-20): the page chrome renders immediately.
    return (
      <div>
        <AcoHeader title="QPS Management" subtitle="Quality Performance Standard — the APP Plus scorecard" showReportBar />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="mt-4 h-14 w-full rounded-lg" />
        <Skeleton className="mt-4 h-96 w-full rounded-lg" />
      </div>
    );
  }
  if (!selectedAco) return <NoAcoState />;
  if (scoring.configError || !measureSet || !constants) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <h2 className="text-xl font-semibold">Reference data problem</h2>
        <p className="mt-2 text-sm text-muted-foreground">{scoring.configError ?? 'Reference data failed to load.'}</p>
      </div>
    );
  }

  const patch = (measureId: string, p: Partial<StoredSubmission>) =>
    setDraft(prev => ({ ...prev, [measureId]: { ...prev[measureId], ...p } }));

  // Effective rows (calc-mode measures carry pipeline counts) + drafts on top.
  const rowFor = (measureId: string): StoredSubmission => ({
    measure_id: measureId, collection_type: 'MIPS CQM', reported: true,
    ...scoring.effectiveEntries[measureId], ...draft[measureId],
  } as StoredSubmission);

  /** STORED row (no pipeline substitution) + drafts — the shape lane memory is recorded from. */
  const storedRowFor = (measureId: string): StoredSubmission => ({
    measure_id: measureId, collection_type: 'MIPS CQM', reported: true,
    ...scoring.entries[measureId], ...draft[measureId],
  } as StoredSubmission);

  /**
   * Collection-type pill click. Lane memory: the lane you were on is
   * remembered (mode + manual counts), the new lane's remembered state — or a
   * default (calc if the pipeline has data for it, else manual) — becomes the
   * row. Whole row goes into the draft so Confirm persists both lanes.
   */
  const selectLane = async (measureId: string, ct: string) => {
    if (!logic) return;
    const next = logic.switchLane(storedRowFor(measureId), ct, scoring.calculated);
    if (draft[measureId]) {
      // The row already has unsaved edits — keep everything in the draft so
      // Confirm persists both the edits and the lane change together.
      setDraft(prev => ({ ...prev, [measureId]: { ...prev[measureId], ...next } }));
      return;
    }
    // Nothing pending on this row: switching lanes is a selection, not an
    // edit (owner 2026-09-12) — persist immediately, like the mode radio, so
    // the row never shows "Pending Changes" just for looking at another lane.
    try {
      await scoring.save([next]);
    } catch (e) {
      toast({ title: 'Could not switch collection type', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    }
  };

  /** Persist a mode flip immediately (manual ⇄ calc), like the MIPS mode radio. */
  const setMode = async (measureId: string, mode: 'manual' | 'calc') => {
    if (!logic) return;
    const stored = scoring.entries[measureId] ?? { measure_id: measureId, collection_type: 'MIPS CQM', reported: true };
    try {
      const row = logic.recordLane({ ...stored, payload: { ...stored.payload, mode } } as StoredSubmission);
      await scoring.save([row]);
      toast({ title: mode === 'calc' ? 'Recalculating from data' : 'Manual entry mode', description: `Measure ${measureId} · ${row.collection_type}` });
    } catch (e) {
      toast({ title: 'Mode change failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      // Record the selected lane's state into lane memory before persisting, so
      // switching collection types later restores exactly what was entered.
      await scoring.save(Object.keys(draft).map(id => (logic ? logic.recordLane(storedRowFor(id)) : storedRowFor(id))));
      setDraft({});
      toast({ title: 'Saved', description: 'Measure data and the recomputed score are persisted.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const fmtMoney = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  // ----- rows: assemble → filter → sort -----
  interface RowModel {
    measure: AppMeasure;
    row: StoredSubmission;
    result: MeasureScoreResult;
    appliedResult: MeasureScoreResult | undefined;
    cell: AppBenchmarkCell;
    pending: boolean;
    isClinical: boolean;
  }

  const score = displayScore;
  const allRows: RowModel[] = score
    ? measureSet.measures.map(m => {
        const row = rowFor(m.measureId);
        const clinicalIds = (logic?.clinicalMeasureIds ?? CLINICAL_IDS) as readonly string[];
        const effectiveCt = ctOverride && clinicalIds.includes(m.measureId)
          ? ctOverride : row.collection_type;
        return {
          measure: m,
          row: { ...row, collection_type: effectiveCt },
          result: score.measures.find(r => r.measureId === m.measureId)!,
          appliedResult: applied?.measures.find(r => r.measureId === m.measureId),
          cell: logic!.getBenchmarkCell(programYear, m.measureId, effectiveCt as never),
          pending: !!draft[m.measureId],
          isClinical: clinicalIds.includes(m.measureId),
        };
      })
    : [];

  const activeFilterCount =
    filters.collectionTypes.length +
    Number(filters.inverse) + Number(filters.outcome) +
    Number(filters.belowCaseMin) + Number(filters.awaiting);

  const filtered = allRows.filter(r => {
    if (filters.collectionTypes.length > 0 && !filters.collectionTypes.includes(r.row.collection_type)) return false;
    if (filters.inverse && !r.measure.isInverse) return false;
    if (filters.outcome && !r.measure.isOutcome) return false;
    if (filters.belowCaseMin && r.result.state !== 'excluded_case_minimum') return false;
    if (filters.awaiting && r.result.displayState !== 'unknowable') return false;
    return true;
  });

  const statusRank = (r: MeasureScoreResult) =>
    r.state === 'unreported' ? 0 : r.displayState === 'unknowable' ? 1 : r.displayState === 'projected' ? 2 : 3;
  const sorted = [...filtered].sort((a, b) => {
    for (const s of sorters) {
      let cmp = 0;
      switch (s.key) {
        case 'id': cmp = a.measure.measureId.localeCompare(b.measure.measureId); break;
        case 'points': cmp = a.result.points - b.result.points; break;
        case 'rate': cmp = (a.result.rate ?? -1) - (b.result.rate ?? -1); break;
        case 'dc': cmp = (a.result.dataCompletenessPct ?? -1) - (b.result.dataCompletenessPct ?? -1); break;
        case 'status': cmp = statusRank(a.result) - statusRank(b.result); break;
      }
      if (cmp !== 0) return s.dir === 'asc' ? cmp : -cmp;
    }
    return 0;
  });

  const sortOptions: Array<{ key: SortKey; label: string }> = [
    { key: 'points', label: 'Est. Points' }, { key: 'rate', label: 'Performance Rate' },
    { key: 'dc', label: 'Complete Rate' }, { key: 'id', label: 'Measure ID' }, { key: 'status', label: 'Status' },
  ];

  const exportSummary = () => {
    if (!score) return;
    const head = 'measureId,collectionType,completeRate,performanceRate,points,status';
    const lines = sorted.map(r =>
      [r.measure.measureId, r.row.collection_type,
        r.result.dataCompletenessPct?.toFixed(2) ?? '', r.result.rate?.toFixed(2) ?? '',
        r.result.points.toFixed(1), r.result.displayState].join(','));
    const qps = [
      '', 'QPS panel', `composite,${score.composite.toFixed(2)}`, `meetsQps,${score.meetsQps}`,
      `effectiveSharingRatePct,${score.effectiveSharingRatePct.toFixed(2)}`,
      `projectedSharedSavings,${score.projectedSharedSavings ?? ''}`,
    ];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([[head, ...lines, ...qps].join('\n')], { type: 'text/csv' }));
    a.download = `app-plus-qps-${selectedAco.aco_id_cms}-${programYear}.csv`;
    a.click();
  };

  /**
   * Patient Details export (D12 remainder): every calculated measure's patient
   * rows from the ACO scope, in the SAME column layout as the MIPS partial-
   * measures export (ACO identity in the practice columns).
   */
  const exportPatientDetails = async () => {
    // R14: one pipeline stream per (base measure, collection type) — export
    // every lane that has data. Medicare CQM lanes fetch by the SSP twin id.
    const lanes = Object.entries(scoring.calculated).flatMap(([baseId, byType]) =>
      Object.values(byType ?? {}).map(counts => ({
        baseId,
        fetchId: counts?.pipelineMeasureId ?? baseId,
        method: counts?.submissionMethod ?? 'MIPS CQM',
      })));
    if (!selectedAco.shadow_practice_id || lanes.length === 0) return;
    setExportingPatients(true);
    try {
      const headers = [
        'Measure_ID', 'Collection_Type', 'ACO_ID', 'ACO_Name', 'TIN', 'Practice_Name', 'Patient_ID', 'Full_Name', 'DOB', 'Gender',
        'NPI', 'NPI_Name', 'Date_of_Service', 'CPT_Codes', 'CPT_Modifiers', 'ICD10_Codes',
        'Place_of_Service', 'Initial_Population', 'Denominator_Exclusion', 'Denominator',
        'Numerator_Met', 'Numerator_Exception', 'Numerator_Not_Met', 'Data_Source', 'Notes',
      ];
      const allRows: string[][] = [];
      for (const { baseId, fetchId, method } of lanes) {
        try {
          const rows = await apiClient.get(
            `/api/performance/${selectedAco.shadow_practice_id}/measures/${encodeURIComponent(fetchId)}/patients?submissionMethod=${encodeURIComponent(method)}&evaluationType=GROUP&year=${programYear}`);
          for (const r of Array.isArray(rows) ? rows : []) {
            allRows.push([
              baseId, method, selectedAco.aco_id_cms, selectedAco.name, r.tin || '', r.tinName || '',
              r.patient_id || r.pid || '', r.full_name || '', r.date_of_birth || r.dob || '',
              r.gender || '', r.npi || '', r.npi_name || r.npiName || '',
              r.date_of_service || r.dos || '', r.cpt_codes || r.cptCodes || '',
              r.cpt_modifiers || r.cptModifiers || '', r.icd10_codes || r.icd10Codes || '',
              r.place_of_service || r.placeOfService || '',
              r.ip ? 'True' : 'False', r.ex ? 'True' : 'False', r.den ? 'True' : 'False',
              r.met ? 'True' : 'False', r.exc ? 'True' : 'False', r.not ? 'True' : 'False',
              r.source || r.data_source || '', r.notes || '',
            ]);
          }
        } catch (e) {
          console.error(`[QPS export] measure ${baseId} (${method}) fetch failed:`, e);
        }
      }
      if (allRows.length === 0) {
        toast({ title: 'No data available', description: 'No patient rows found for the calculated measures.', variant: 'destructive' });
        return;
      }
      const esc = (v: string) => /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      const csv = [headers.join(','), ...allRows.map(row => row.map(v => esc(String(v ?? ''))).join(','))].join('\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      a.download = `app-plus-patient-details-${selectedAco.aco_id_cms}-${programYear}.csv`;
      a.click();
      toast({ title: 'Export complete', description: `Exported ${allRows.length} patient records from ${lanes.length} measure stream(s).` });
    } finally {
      setExportingPatients(false);
    }
  };

  const downloadMeasureDetail = (r: RowModel) => {
    const lines = [
      'field,value',
      `measureId,${r.measure.measureId}`, `title,"${r.measure.name}"`,
      `collectionType,${r.row.collection_type}`,
      `eligiblePopulation,${r.row.eligible_population ?? ''}`,
      `exclusions,${r.row.denominator_exclusions ?? ''}`,
      `met,${r.row.performance_met ?? ''}`, `exceptions,${r.row.denominator_exceptions ?? ''}`,
      `notMet,${r.row.performance_not_met ?? ''}`,
      `performanceRate,${r.result.rate?.toFixed(2) ?? ''}`,
      `completeRate,${r.result.dataCompletenessPct?.toFixed(2) ?? ''}`,
      `points,${r.result.points.toFixed(1)}`, `status,${r.result.displayState}`, `detail,"${r.result.detail}"`,
    ];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `app-plus-${r.measure.measureId}-${programYear}.csv`;
    a.click();
  };

  const simLabel = simValue === 'off' ? 'Off'
    : simValue.startsWith('delta:') ? `+${simValue.split(':')[1]}% Perf Rate`
    : simValue === 'rule:no75' ? 'Waive 75% Completeness'
    : simValue === 'rule:noden' ? 'Waive Case Minimum'
    : simValue === 'rule:both' ? 'Waive Both Requirements'
    : `All ${simValue.slice(3)}`;

  // Ordered visible columns — the single driver for header AND body cells.
  const orderedVisible: QpsColKey[] = colSettings.order.filter(k => colSettings.visibility[k]);
  const strataConfig = STRATA_BY_YEAR[programYear] ?? {};

  const sheetRow = sheetId ? sorted.find(r => r.measure.measureId === sheetId) ?? allRows.find(r => r.measure.measureId === sheetId) : null;

  // ----- order-driven table cells (header + body render from orderedVisible) -----
  const countHead = (label: string, tip: string) => (
    <TableHead className="text-center"><CellTip on={tooltips} tip={tip}><span>{label}</span></CellTip></TableHead>
  );
  const headerCell = (key: QpsColKey): JSX.Element => {
    switch (key) {
      case 'id': return <TableHead key={key} className="w-36"><CellTip on={tooltips} tip="CMS quality measure number — click it to open the measure sheet (details + data entry)"><span>Measure ID</span></CellTip></TableHead>;
      case 'title': return <TableHead key={key} className="text-left min-w-28 xl:min-w-32 2xl:min-w-48"><CellTip on={tooltips} tip="CMS measure name"><span>Title</span></CellTip></TableHead>;
      case 'flags': return <TableHead key={key} className="text-center"><CellTip on={tooltips} tip="Measure attributes: inverse (lower is better), outcome (alternative-standard leg), data source, and benchmark kind"><span>Flags</span></CellTip></TableHead>;
      case 'ct': return <TableHead key={key} className="text-center min-w-44"><CellTip on={tooltips} tip="How the measure is collected and reported. Selectable per clinical measure — benchmarks (and whether one exists yet) differ by collection type"><span>Collection Type</span></CellTip></TableHead>;
      case 'cmsAvg': return <TableHead key={key} className="text-center"><CellTip on={tooltips} tip="CMS national average performance rate for the selected collection type's benchmark"><span>CMS Avg</span></CellTip></TableHead>;
      case 'ip': return <Fragment key={key}>{countHead('Eligible Pop', "Patients meeting the measure's denominator criteria")}</Fragment>;
      case 'ex': return <Fragment key={key}>{countHead('Excl', 'Denominator exclusions')}</Fragment>;
      case 'den': return <Fragment key={key}>{countHead('Den', 'Denominator = Eligible Pop − Exclusions (calculated)')}</Fragment>;
      case 'met': return <Fragment key={key}>{countHead('Met', 'Performance met')}</Fragment>;
      case 'exc': return <Fragment key={key}>{countHead('Exc', 'Denominator exceptions')}</Fragment>;
      case 'not': return <Fragment key={key}>{countHead('Not Met', 'Performance not met')}</Fragment>;
      case 'dc': return <TableHead key={key} className="text-center"><CellTip on={tooltips} tip={`Data completeness = (Met + Not Met + Exceptions) / Denominator. At least ${constants.dataCompletenessThreshold}% is required`}><span>Complete Rate</span></CellTip></TableHead>;
      case 'rate': return <TableHead key={key} className="text-center min-w-28 xl:min-w-32 2xl:min-w-48"><CellTip on={tooltips} tip="Performance rate = Met / (Met + Not Met), shown against the decile benchmark for the selected collection type"><span>Performance Rate</span></CellTip></TableHead>;
      case 'points': return <TableHead key={key} className="text-center"><CellTip on={tooltips} tip="Projected points for the current data (hover a value for the engine's explanation)"><span>Est. Points</span></CellTip></TableHead>;
      case 'goal': return <TableHead key={key} className="text-center"><CellTip on={tooltips} tip="Whether the measure currently clears its CMS quality goal — the 10th percentile for outcome measures, the 40th percentile otherwise (the alternative-standard legs)"><span>Goal</span></CellTip></TableHead>;
      case 'actions': return <TableHead key={key} className="quality-print-hide text-center">Actions</TableHead>;
    }
  };

  return (
    <div>
      <AcoHeader title="QPS Management" subtitle="Quality Performance Standard — the APP Plus scorecard" showReportBar />

      {score && (
        <>
          {/* ===== Score strip (owner-approved as built — do not regress) ===== */}
          <div
            className="rounded-lg border bg-card p-4 cursor-pointer transition-colors hover:bg-muted/50"
            onClick={() => setStripOpen(o => !o)}
            role="button"
            aria-expanded={stripOpen}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    Quality Composite
                    {dirty && <Badge variant="secondary" className="ml-1 align-middle">WHAT-IF</Badge>}
                    {simActive && <Badge variant="secondary" className="ml-1 align-middle border-blue-300 text-blue-700 bg-blue-50">SIMULATED</Badge>}
                  </p>
                  <p className="text-[30px] leading-[45px] font-semibold mt-0.5">
                    {score.composite.toFixed(2)}
                    {score.floorApplied && (
                      <Badge variant="secondary" className="ml-2 align-middle"
                        title={`Raw ${score.rawComposite.toFixed(2)} — floored: ${score.floorReasons.join('; ')}`}>
                        FLOORED
                      </Badge>
                    )}
                    {score.meetsQps
                      ? <Badge className="ml-2 align-middle bg-emerald-600">MEETS QPS</Badge>
                      : <Badge variant="outline" className="ml-2 align-middle text-amber-600 border-amber-400">BELOW QPS</Badge>}
                  </p>
                </div>
                <div className="h-10 w-px bg-border" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Projected Shared Savings</p>
                  <p className={cn('text-[24px] leading-[45px] font-semibold mt-0.5',
                    score.projectedSharedSavings != null && score.projectedSharedSavings > 0 ? 'text-green-600' : 'text-muted-foreground')}>
                    {score.projectedSharedSavings != null ? `+${fmtMoney(score.projectedSharedSavings)}` : '—'}
                  </p>
                </div>
                <div className="h-10 w-px bg-border" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Effective Sharing Rate</p>
                  <p className="text-[24px] leading-[45px] font-semibold mt-0.5">
                    {score.effectiveSharingRatePct.toFixed(1)}%
                    <span className="ml-1.5 align-middle text-xs font-normal text-muted-foreground">
                      of a {constants.sharingRatePct[scoring.engineConfig.track]}% track max
                    </span>
                  </p>
                </div>
                <div className="h-10 w-px bg-border" />
                <div>
                  {/* Owner 2026-07-20: SEPARATE from the effective rate — the share of
                      the track's full rate the quality score is earning. */}
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">QPS Scaled Rate</p>
                  <p className="text-[24px] leading-[45px] font-semibold mt-0.5">
                    {(() => {
                      const trackMax = constants.sharingRatePct[scoring.engineConfig.track];
                      const pct = trackMax > 0 ? (score.effectiveSharingRatePct / trackMax) * 100 : 0;
                      return `${pct.toFixed(1)}%`;
                    })()}
                    <span className="ml-1.5 align-middle text-xs font-normal text-muted-foreground">of the full rate</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Details</span>
                {stripOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </div>
            {stripOpen && (
              <div className="mt-3 grid gap-2 border-t pt-3 text-sm text-muted-foreground sm:grid-cols-3" onClick={e => e.stopPropagation()}>
                <div>
                  <span className="font-medium text-foreground">Points:</span>{' '}
                  {score.measurePointsSum.toFixed(1)} + {score.complexOrgBonus} Complex-Org bonus
                  {score.improvementScore > 0 && <> + {score.improvementScore.toFixed(1)} improvement</>} / {score.availablePoints} available
                </div>
                <div>
                  <span className="font-medium text-foreground">Loss rate (two-sided):</span>{' '}
                  {score.lossRatePct != null ? `${score.lossRatePct.toFixed(2)}%` : 'n/a for this track'}
                </div>
                <div>
                  <span className="font-medium text-foreground">Payout cap:</span>{' '}
                  {score.paymentCapPct}% of updated benchmark
                </div>
              </div>
            )}
          </div>

          {/* ===== Path to QPS — decluttered (owner item 5, 2026-07-14 PM):
                 one verdict line + compact pathway chips; sentences expand on demand ===== */}
          <div className="mt-4 rounded-lg border bg-card">
            <button
              className="flex w-full flex-wrap items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-muted/40 rounded-lg"
              onClick={() => setPathOpen(o => !o)}
              aria-expanded={pathOpen}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold">Path to QPS</p>
                <p className="truncate text-sm text-muted-foreground">
                  {score.meetsQps
                    ? <>You qualify for shared savings at the <span className="font-medium text-foreground">full {constants.sharingRatePct[scoring.engineConfig.track]}% rate</span>.</>
                    : (score.alternative.status === 'met' || score.alternative.status === 'projected')
                      ? <>Below the QPS — the alternative standard keeps you eligible at a <span className="font-medium text-foreground">scaled rate ({score.effectiveSharingRatePct.toFixed(1)}%)</span>.</>
                      : scoring.engineConfig.track === 'ENHANCED'
                        ? <>Below the QPS and the alternative standard — no shared savings, and <span className="font-medium text-red-600">maximum shared losses ({score.lossRatePct?.toFixed(0) ?? 75}%) apply</span> (ENHANCED).</>
                        : 'Below the QPS and the alternative standard — no shared savings at the current numbers.'}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* §6.4 metadata-pill recipe (slate); status carried by the small icon only */}
                {([['Pathway 1', score.pathway1], ['Pathway 2', score.pathway2], ['Pathway 3', score.pathway3], ['Alt standard', score.alternative]] as const).map(([label, p]) => (
                  <span key={label} title={tooltips ? p.reason : undefined}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700 whitespace-nowrap">
                    <span aria-hidden className={cn('font-semibold',
                      p.status === 'met' ? 'text-emerald-600'
                        : p.status === 'projected' ? 'text-blue-600'
                        : p.status === 'unknowable' ? 'text-amber-600'
                        : 'text-slate-400')}>
                      {p.status === 'met' ? '✓' : p.status === 'projected' ? '≈' : p.status === 'unknowable' ? '?' : '✗'}
                    </span>
                    {label}
                  </span>
                ))}
                {pathOpen ? <ChevronDown className="ml-1 h-4 w-4 text-muted-foreground" /> : <ChevronRight className="ml-1 h-4 w-4 text-muted-foreground" />}
              </div>
            </button>
            {pathOpen && (
              <ul className="space-y-1.5 border-t px-4 py-3 text-sm text-muted-foreground">
                {([['p1', score.pathway1], ['p2', score.pathway2], ['p3', score.pathway3], ['alt', score.alternative]] as const).map(([id, p]) => (
                  <li key={id} className="flex items-start gap-2">
                    <span className={cn('mt-0.5 font-semibold',
                      p.status === 'met' ? 'text-emerald-600' : p.status === 'projected' ? 'text-blue-600'
                      : p.status === 'disqualified' || p.status === 'not_met' ? 'text-muted-foreground' : 'text-red-600')}>
                      {p.status === 'met' ? '✓' : p.status === 'projected' ? '≈' : p.status === 'unknowable' ? '?' : '✗'}
                    </span>
                    <span title={tooltips ? p.reason : undefined}>
                      {pathwaySentence(id, p, constants.qpsThreshold, score.composite,
                        id === 'p3' ? pathway3Blockers(score.measures, constants.dataCompletenessThreshold) : undefined)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ===== Actions card — the MIPS Gap Management Actions card, ACO-scoped ===== */}
          <AcoActionsCard acoId={selectedAco.id} />

          {/* ===== Table card — the MIPS Quality Summary card header verbatim (§6.8) ===== */}
          <Card className="mt-4">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-2xl font-bold">Quality Summary</CardTitle>
                </div>
                <div className="quality-print-hide flex flex-wrap items-center gap-2 text-sm w-full lg:w-auto lg:ml-auto">
                  <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                    <div className="flex flex-col lg:flex-row items-center gap-1 lg:gap-2">
                      <span className="text-xs lg:text-sm text-muted-foreground">Tooltips</span>
                      <Switch checked={tooltips} onCheckedChange={v => setTooltips(!!v)} />
                    </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Columns className="mr-2 h-4 w-4" /> View <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50 bg-background">
                    <DropdownMenuLabel>Column Views</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={activePresetId} onValueChange={applyPreset}>
                      {SYSTEM_PRESETS.map(p => (
                        <DropdownMenuRadioItem key={p.id} value={p.id}>{p.name}</DropdownMenuRadioItem>
                      ))}
                      {userViews.length > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs">My Views</DropdownMenuLabel>
                          {userViews.map(v => (
                            <DropdownMenuRadioItem key={v.id} value={v.id}>{v.name}</DropdownMenuRadioItem>
                          ))}
                        </>
                      )}
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setManageViewsOpen(true)}>
                      Manage Views…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Simulate Scoring Scenario
                      <span className="ml-2 rounded border px-1.5 py-0.5 text-xs bg-muted/50">{simLabel}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50 bg-background">
                    <DropdownMenuLabel>Simulate Scoring Scenario</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={simValue} onValueChange={setSimValue}>
                      <DropdownMenuLabel className="text-xs opacity-70">Performance rate increase</DropdownMenuLabel>
                      <DropdownMenuRadioItem value="off">Off</DropdownMenuRadioItem>
                      {['2.5', '5', '10', '15', '20'].map(d => (
                        <DropdownMenuRadioItem key={d} value={`delta:${d}`}>+{d}% Perf Rate</DropdownMenuRadioItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs opacity-70">What-if scenarios</DropdownMenuLabel>
                      {([
                        ['rule:no75', 'Waive 75% Completeness', 'Shows potential points if the 75% data completeness requirement was waived.'],
                        ['rule:noden', 'Waive Case Minimum', 'Shows potential points if the case minimum was waived.'],
                        ['rule:both', 'Waive Both Requirements', 'Shows maximum potential points if both requirements were waived.'],
                      ] as const).map(([v, label, tip]) => (
                        <DropdownMenuRadioItem key={v} value={v}>
                          <div className="flex w-full items-center justify-between gap-2">
                            <span>{label}</span>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild><CircleHelp className="h-3.5 w-3.5" /></TooltipTrigger>
                                <TooltipContent className="max-w-xs">{tip}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </DropdownMenuRadioItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs opacity-70">Collection-type strategy (clinical measures)</DropdownMenuLabel>
                      {CLINICAL_TYPES.map(ct => (
                        <DropdownMenuRadioItem key={ct} value={`ct:${ct}`}>All {ct}</DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button variant="outline" size="sm" onClick={() => setSortOpen(o => !o)}>
                  <ArrowUpDown className="mr-2 h-4 w-4" />
                  Sort & Filter ({sorters.length + activeFilterCount})
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="mr-2 h-4 w-4" /> Export
                      {pendingCount > 0 && <Badge className="ml-2" variant="secondary">{pendingCount}</Badge>}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-50 bg-background">
                    <DropdownMenuItem onClick={exportSummary}>Export Summary (CSV)</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={Object.keys(scoring.calculated).length === 0 || exportingPatients}
                      title={Object.keys(scoring.calculated).length === 0 ? 'Upload ACO claims data first (Data Management)' : undefined}
                      onClick={exportPatientDetails}>
                      {exportingPatients ? 'Exporting patients…' : 'Patient Details (CSV)'}
                      {Object.keys(scoring.calculated).length === 0 && (
                        <span className="ml-2 text-xs text-muted-foreground">(needs uploaded data)</span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setAbstractionModalOpen(true)} disabled={pendingCount === 0}>
                      Export Chart Abstraction List
                      {pendingCount > 0
                        ? <Badge className="ml-auto" variant="default">{pendingCount}</Badge>
                        : <span className="ml-auto text-xs text-muted-foreground">0 pending</span>}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">

            {/* Sort & Filter panel (§6.9) */}
            {sortOpen && (
              <div className="mx-4 mb-4 rounded-lg border bg-muted/30 p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium">Sort</div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setSorters([]); setFilters(NO_FILTERS); }}>Clear all</Button>
                    <Button size="sm" onClick={() => setSortOpen(false)}>Apply</Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {sorters.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-1 rounded-md border px-2 py-1 bg-background">
                      <Select value={s.key} onValueChange={(v) => setSorters(prev => prev.map((p, i) => i === idx ? { ...p, key: v as SortKey } : p))}>
                        <SelectTrigger className="h-8 w-[170px]"><SelectValue placeholder="Field" /></SelectTrigger>
                        <SelectContent className="z-50 bg-background">
                          {sortOptions.map(o => (<SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => setSorters(prev => prev.map((p, i) => i === idx ? { ...p, dir: p.dir === 'asc' ? 'desc' : 'asc' } : p))}
                        aria-label="Toggle sort direction">
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => setSorters(prev => prev.filter((_, i) => i !== idx))} aria-label="Remove sort">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setSorters(prev => [...prev, { key: 'points', dir: 'desc' }])}>+ Add sort</Button>
                </div>
                <Separator />
                <div className="font-medium">Filters</div>
                <div className="flex flex-wrap items-center gap-2">
                  {CLINICAL_TYPES.map(ct => (
                    <Button key={ct} type="button" size="sm"
                      variant={filters.collectionTypes.includes(ct) ? 'default' : 'outline'}
                      onClick={() => setFilters(prev => ({
                        ...prev,
                        collectionTypes: prev.collectionTypes.includes(ct)
                          ? prev.collectionTypes.filter(c => c !== ct)
                          : [...prev.collectionTypes, ct],
                      }))}>
                      {ct}
                    </Button>
                  ))}
                  {([
                    ['inverse', 'Inverse'], ['outcome', 'Outcome measures'],
                    ['belowCaseMin', 'Below case minimum'], ['awaiting', 'Awaiting benchmark'],
                  ] as const).map(([key, label]) => (
                    <Button key={key} type="button" size="sm"
                      variant={filters[key] ? 'default' : 'outline'}
                      onClick={() => setFilters(prev => ({ ...prev, [key]: !prev[key] }))}>
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* §6.7 table — columns render from the active view's order/visibility */}
            <Table>
              <TableHeader>
                <TableRow>
                  {orderedVisible.map(headerCell)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r, i) => {
                  const m = r.measure;
                  const hasStrata = !!strataConfig[m.measureId];
                  const isOpen = !!expanded[m.measureId];
                  const den = (r.row.eligible_population ?? 0) - (r.row.denominator_exclusions ?? 0);
                  const gain = (dirty || simActive) && r.appliedResult ? r.result.points - r.appliedResult.points : 0;
                  const deciles = decilesForCell(r.cell);
                  const cmsAvg = cmsAvgFor(r.cell);
                  const dcLow = r.result.dataCompletenessPct != null && r.result.dataCompletenessPct < constants.dataCompletenessThreshold;

                  // One source for the row's flags — rendered as pills in the Flags
                  // COLUMN when it's visible, else as a muted sub-line under the
                  // Title (owner 2026-07-20 PM).
                  const flagDefs: Array<{ label: string; tip: string; tone: 'slate' | 'amber' | 'blue' }> = [
                    ...(m.isInverse ? [{ label: 'Inverse', tone: 'slate' as const,
                      tip: 'Inverse measure — a LOWER performance rate is better' }] : []),
                    ...(m.isOutcome ? [{ label: 'Outcome', tone: 'slate' as const,
                      tip: 'Outcome measure — counts for the 10th-percentile alternative-standard leg' }] : []),
                    ...(r.result.displayState === 'unknowable' ? [{ label: 'No Bnchmrk', tone: 'amber' as const,
                      tip: 'CMS hasn\u2019t published a benchmark for this collection type yet — nobody can be scored on it. The measure is excluded and the regulatory floor protects your composite.' }] : []),
                    ...(r.result.state === 'scored' && r.result.displayState === 'projected' ? [{ label: 'Projected', tone: 'blue' as const,
                      tip: 'Based on your current estimate — CMS will publish the real value later. A projected number can\u2019t secure the QPS by itself.' }] : []),
                    ...(r.result.state === 'unreported' ? [{ label: 'Not Reported', tone: 'amber' as const,
                      tip: 'Not reported — scores 0 points and stays in the available points.' }] : []),
                    ...(r.result.state === 'excluded_case_minimum' ? [{ label: 'Below Case Min', tone: 'amber' as const,
                      tip: 'Below the case minimum — excluded from the composite; available points shrink.' }] : []),
                  ];
                  const flagsColumnVisible = orderedVisible.includes('flags');

                  const countCell = (v: React.ReactNode) => (
                    <TableCell className="text-center max-w-20 tabular-nums">{v}</TableCell>
                  );
                  const bodyCell = (key: QpsColKey): JSX.Element | null => {
                    switch (key) {
                      case 'id': return (
                        <TableCell key={key}>
                          <div className="flex items-center gap-1">
                            <span className="inline-flex w-6 justify-center">
                              {hasStrata && (
                                <Button variant="ghost" className="h-6 w-6 p-0"
                                  onClick={() => setExpanded(prev => ({ ...prev, [m.measureId]: !isOpen }))}>
                                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              )}
                            </span>
                            <CellTip on={tooltips} tip="Opens the measure sheet — details, benchmark, and data entry">
                              <Button variant="link" className="h-auto p-0 text-xl font-black text-primary hover:text-primary/80"
                                onClick={() => setSheetId(m.measureId)}>
                                {m.measureId}
                              </Button>
                            </CellTip>
                          </div>
                        </TableCell>
                      );
                      case 'title': return (
                        <TableCell key={key} className="text-left max-w-80 whitespace-normal leading-tight">
                          <CellTip on={tooltips} tip={m.name}>
                            <span className="line-clamp-1" title={tooltips ? undefined : m.name}>{m.name}</span>
                          </CellTip>
                          {/* Flags sub-line when the Flags column is hidden (Summary view) */}
                          {!flagsColumnVisible && flagDefs.length > 0 && (
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-1 text-[11px] leading-4">
                              {flagDefs.map((f, fi) => (
                                <Fragment key={f.label}>
                                  {fi > 0 && <span className="text-muted-foreground/50">·</span>}
                                  <CellTip on={tooltips} tip={f.tip}>
                                    <span className={cn(
                                      f.tone === 'amber' && 'text-amber-700 font-medium',
                                      f.tone === 'blue' && 'text-blue-700 font-medium',
                                      f.tone === 'slate' && 'text-muted-foreground',
                                    )}>{f.label}</span>
                                  </CellTip>
                                </Fragment>
                              ))}
                            </div>
                          )}
                        </TableCell>
                      );
                      case 'flags': return (
                        <TableCell key={key} className="text-center">
                          <div className="flex flex-wrap items-center justify-center gap-1">
                            {flagDefs.map(f => (
                              <CellTip key={f.label} on={tooltips} tip={f.tip}>
                                <MetaPill tone={f.tone}>{f.label}</MetaPill>
                              </CellTip>
                            ))}
                          </div>
                        </TableCell>
                      );
                      case 'ct': return (
                        // whitespace-nowrap + header min-width: the group renders
                        // IDENTICALLY in every view — no width-driven wrapping
                        // (owner 2026-07-20 PM: "all should be uniform").
                        <TableCell key={key} className="text-center whitespace-nowrap">
                          {/* Horizontal joined selector (owner reverted the vertical variant 2026-07-14 PM) */}
                          {r.isClinical ? (
                            <div className="inline-flex flex-nowrap rounded-full border border-border overflow-hidden">
                              {CLINICAL_TYPES.map(ct => {
                                // Lane memory indicator: ● pipeline data available · ○ manual counts saved · nothing = untouched
                                const status = logic ? logic.laneStatus(storedRowFor(m.measureId), ct, scoring.calculated) : 'none';
                                const lane = ct === r.row.collection_type ? null : r.row.payload?.lanes?.[ct];
                                const tip = ctOverride
                                  ? 'Collection types are pinned by the active Simulate scenario'
                                  : status === 'data'
                                    ? `${ct}: calculated data available from the ACO upload`
                                    : status === 'manual'
                                      ? `${ct}: manual counts saved${lane ? ` (${lane.performance_met ?? 0} met of ${(lane.performance_met ?? 0) + (lane.performance_not_met ?? 0)})` : ''}`
                                      : `${ct}: nothing entered yet`;
                                return (
                                  <button key={ct} disabled={isCurrentYearLocked || !!ctOverride}
                                    title={tip}
                                    onClick={() => selectLane(m.measureId, ct)}
                                    className={cn('inline-flex items-center gap-1 px-2 py-0.5 text-[11px] leading-4 transition-colors border-r border-border last:border-r-0',
                                      r.row.collection_type === ct
                                        ? 'bg-primary text-primary-foreground font-medium'
                                        : 'bg-background text-muted-foreground hover:bg-accent')}>
                                    {ct === 'Medicare CQM' ? 'Med CQM' : ct}
                                    {status !== 'none' && (
                                      <span aria-hidden className={cn('inline-block h-1.5 w-1.5 rounded-full',
                                        status === 'data'
                                          ? (r.row.collection_type === ct ? 'bg-primary-foreground' : 'bg-primary')
                                          : (r.row.collection_type === ct ? 'ring-1 ring-primary-foreground' : 'ring-1 ring-primary'))} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            // Fixed types: SAME frame as the selector but INACTIVE
                            // styling (owner 2026-07-20 PM, option a) — navy keeps
                            // meaning "your selection among options"; nothing is
                            // being chosen here.
                            <div className="inline-flex rounded-full border border-border overflow-hidden">
                              <span className="px-2 py-0.5 text-[11px] leading-4 bg-background text-muted-foreground cursor-default"
                                title="Fixed collection type — CMS specifies how this measure is collected">
                                {fixedTypeLabel(m)}
                              </span>
                            </div>
                          )}
                        </TableCell>
                      );
                      case 'cmsAvg': return (
                        <TableCell key={key} className="text-center tabular-nums max-w-20">
                          <CellTip on={tooltips} tip="CMS national average performance rate for this measure's benchmark (the selected collection type)">
                            <span>{cmsAvg != null ? `${cmsAvg.toFixed(1)}%` : '—'}</span>
                          </CellTip>
                        </TableCell>
                      );
                      case 'ip': return <Fragment key={key}>{countCell(r.isClinical ? (r.row.eligible_population ?? '—') : '—')}</Fragment>;
                      case 'ex': return <Fragment key={key}>{countCell(r.isClinical ? (r.row.denominator_exclusions ?? '—') : '—')}</Fragment>;
                      case 'den': return <Fragment key={key}>{countCell(r.isClinical && r.row.eligible_population != null ? den : '—')}</Fragment>;
                      case 'met': return <Fragment key={key}>{countCell(r.isClinical ? (r.row.performance_met ?? '—') : '—')}</Fragment>;
                      case 'exc': return <Fragment key={key}>{countCell(r.isClinical ? (r.row.denominator_exceptions ?? '—') : '—')}</Fragment>;
                      case 'not': return <Fragment key={key}>{countCell(r.isClinical ? (r.row.performance_not_met ?? '—') : '—')}</Fragment>;
                      case 'dc': return (
                        <TableCell key={key} className="text-center tabular-nums">
                          <CellTip on={tooltips}
                            tip={`Data completeness = (Met + Not Met + Exceptions) / Denominator. At least ${constants.dataCompletenessThreshold}% is required — below it the measure scores ${scoring.engineConfig.isSmallPractice ? '3' : '0'} points.`}>
                            {r.result.dataCompletenessPct != null ? (
                              <span className={cn(dcLow && 'font-medium text-red-600')}>
                                {r.result.dataCompletenessPct.toFixed(2)}%
                              </span>
                            ) : <span>—</span>}
                          </CellTip>
                        </TableCell>
                      );
                      case 'rate': return (
                        <TableCell key={key} className="text-center">
                          {/* Fixed-height container in EVERY branch — the rate cell is
                              the tallest cell, so pinning it equalizes all row heights
                              (owner 2026-07-20 PM). */}
                          <div className="flex h-14 w-full items-center justify-center">
                          {r.result.rate != null ? (
                            // ALWAYS the benchmark bar when a rate exists — MIPS
                            // behavior (owner 2026-07-20): with no published deciles
                            // (112/113 eCQM/MIPS CQM) the bar renders its neutral
                            // gray track with the performance marker.
                            <DecileBenchmarkBar
                              value={r.result.rate}
                              deciles={deciles}
                              inverse={m.isInverse}
                              variant="segmented"
                              cmsAvg={cmsAvg ?? undefined}
                            />
                          ) : !r.isClinical && r.result.state === 'unreported' ? (
                            // Nothing entered yet — deep-link to the RIGHT tab
                            // (owner 2026-07-20: claims measures → Claims tab).
                            <Button variant="link" size="sm" className="h-auto p-0 text-xs text-muted-foreground hover:text-primary"
                              onClick={() => navigate(`/programs/app-plus/configuration?tab=${m.measureId === '321' ? 'cahps' : 'claims'}`)}>
                              Enter in Configuration →
                            </Button>
                          ) : r.result.state === 'scored' ? (
                            // Scored but rateless (479/484 SD-ladder mode) — the link
                            // would be misleading; data IS entered (owner 2026-07-20 PM).
                            <CellTip on={tooltips} tip="No performance rate for this scoring mode — points come from CMS parameters via the SD ladder (the Projected flag applies).">
                              <span className="text-muted-foreground">—</span>
                            </CellTip>
                          ) : '—'}
                          </div>
                        </TableCell>
                      );
                      case 'points': return (
                        <TableCell key={key} className="text-center">
                          <CellTip on={tooltips} tip={r.result.detail}>
                            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-sm font-bold bg-slate-100 text-slate-800 border border-slate-200 shadow-sm">
                              {r.result.points.toFixed(1)}
                            </span>
                          </CellTip>
                          {(dirty || simActive) && Math.abs(gain) >= 0.05 && (
                            <span className={cn('ml-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap border',
                              gain > 0 ? 'bg-success/10 text-emerald-700 border-emerald-300' : 'bg-red-50 text-red-700 border-red-200')}>
                              <span aria-hidden>{gain > 0 ? '▲' : '▼'}</span>{gain > 0 ? '+' : ''}{gain.toFixed(1)}
                            </span>
                          )}
                        </TableCell>
                      );
                      case 'goal': return (
                        // The CMS quality-goal flag (owner 2026-07-20) — replaces the old Status column
                        <TableCell key={key} className="text-center">
                          <GoalFlag result={r.result} isOutcome={m.isOutcome}
                            tenthMin={constants.percentileLegTenthMinPoints}
                            fortiethMin={constants.percentileLegFortiethMinPoints} />
                        </TableCell>
                      );
                      case 'actions': return (
                        <TableCell key={key} className="quality-print-hide">
                          {/* Lean like MIPS (owner 2026-07-20): mode badge + ⋯ only —
                              attention states are FLAGS now */}
                          <div className="flex items-center gap-1.5 whitespace-nowrap justify-end">
                            {actionsCellBody()}
                          </div>
                        </TableCell>
                      );
                    }
                  };

                  // Mode badges keep MIPS terminology (Calculated/Manual) and now
                  // carry tooltips (owner 2026-07-20).
                  const modeBadge = () => {
                    let badge: JSX.Element;
                    let tip: string;
                    if (r.pending) {
                      badge = <Badge variant="outline" className="!px-2 !py-0.5 !text-xs !leading-none !h-auto border-amber-400 text-amber-600">Pending Changes</Badge>;
                      tip = 'Unsaved what-if edits — Confirm Updates & Recalculate below to persist them.';
                    } else if (r.row.payload?.mode === 'calc') {
                      const hasLaneData = !!scoring.calculated[m.measureId]?.[r.row.collection_type];
                      if (hasLaneData) {
                        badge = <Badge variant="secondary" className="!px-2 !py-0.5 !text-xs !leading-none !h-auto bg-primary/10 text-primary">Calculated</Badge>;
                        tip = 'Counts come from the ACO\u2019s uploaded claims data (pipeline-calculated). Switch modes in the ⋯ menu.';
                      } else {
                        badge = <Badge variant="outline" className="!px-2 !py-0.5 !text-xs !leading-none !h-auto border-amber-400 text-amber-600">Calculated — no data</Badge>;
                        tip = `No ${r.row.collection_type} data uploaded for this measure, so counts show 0. Upload a ${r.row.collection_type} file in Data Management or switch to Manual entry in the ⋯ menu.`;
                      }
                    } else {
                      badge = <Badge variant="secondary" className="!px-2 !py-0.5 !text-xs !leading-none !h-auto">Manual</Badge>;
                      tip = 'Counts are hand-entered in the measure sheet. Switch to \u201CRecalculate from data\u201D in the ⋯ menu once claims data is uploaded.';
                    }
                    return (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild><span className="inline-flex cursor-default">{badge}</span></TooltipTrigger>
                          <TooltipContent className="max-w-xs">{tip}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  };

                  const actionsCellBody = () => (
                    <>
                      {modeBadge()}
                      {actionsMenu()}
                    </>
                  );

                  const actionsMenu = () => (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Row actions" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="z-50 bg-background" align="end">
                                <DropdownMenuItem onClick={() => downloadMeasureDetail(r)}>Download Details</DropdownMenuItem>
                                {(() => {
                                  // R14 Phase E: calc-mode counts are LANE-SCOPED — only data
                                  // uploaded under the row's selected collection type counts.
                                  const rowType = r.row.collection_type;
                                  const laneCounts = scoring.calculated[m.measureId]?.[rowType];
                                  const anyLane = Object.values(scoring.calculated[m.measureId] ?? {})[0];
                                  const hasCalc = !!laneCounts;
                                  // Detail route id is COMPOSITE: "measureId:submissionMethod"
                                  // (QualityWrapper parses it). Prefer the selected lane's
                                  // pipeline stream (SSP twin id for Medicare CQM); fall back
                                  // to any lane with data, then to base-id MIPS CQM — like
                                  // MIPS, the page is NOT gated on data (own empty state).
                                  const detailLane = laneCounts ?? anyLane;
                                  const detailId = detailLane?.pipelineMeasureId ?? m.measureId;
                                  const method = detailLane?.submissionMethod ?? 'MIPS CQM';
                                  return (<>
                                    <DropdownMenuItem
                                      onClick={() => navigate(
                                        `/programs/app-plus/measure/${encodeURIComponent(`${detailId}:${method}`)}?evaluationType=GROUP&practiceId=${selectedAco.shadow_practice_id}`)}>
                                      View Details
                                    </DropdownMenuItem>
                                    <ChartAbstractionActionMenuItem measureId={m.measureId} />
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel>Mode</DropdownMenuLabel>
                                    <DropdownMenuRadioGroup
                                      value={r.row.payload?.mode === 'calc' ? 'calc' : 'manual'}
                                      onValueChange={(v) => {
                                        if (isCurrentYearLocked) return;
                                        setMode(m.measureId, v as 'manual' | 'calc');
                                      }}>
                                      <DropdownMenuRadioItem value="manual">Manual entry</DropdownMenuRadioItem>
                                      {/* Always selectable for clinical measures (owner
                                          2026-07-21): with no {rowType} data uploaded the
                                          row honestly shows zeros, never a stale snapshot. */}
                                      <DropdownMenuRadioItem value="calc" disabled={!r.isClinical}
                                        title={hasCalc ? undefined
                                          : `No ${rowType} data uploaded for this measure — counts show 0 until a ${rowType} file is uploaded in Data Management`}>
                                        Recalculate from data ({rowType})
                                      </DropdownMenuRadioItem>
                                    </DropdownMenuRadioGroup>
                                  </>);
                                })()}
                                {r.isClinical && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem disabled={isCurrentYearLocked}
                                      onClick={() => patch(m.measureId, { reported: !(r.row.reported !== false) })}>
                                      {r.row.reported !== false ? 'Mark not reported' : 'Mark reported'}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {!r.isClinical && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate(`/programs/app-plus/configuration?tab=${m.measureId === '321' ? 'cahps' : 'claims'}`)}>
                                      Enter in Configuration →
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                  );

                  return (
                    <Fragment key={m.measureId}>
                      <TableRow className={cn(
                        'h-14 hover:bg-muted/50 transition-colors',
                        r.pending && 'opacity-90 !border-l-4 !border-l-amber-500',
                        !r.pending && (i % 2 === 0 ? 'bg-background' : 'bg-muted/20'),
                      )}>
                        {orderedVisible.map(bodyCell)}
                      </TableRow>

                      {/* D11: config-driven strata expansion (v1: 321's SSMs, read-only) */}
                      {hasStrata && isOpen && (
                        <TableRow className={cn('hover:bg-muted/30 transition-colors',
                          i % 2 === 0 ? 'bg-muted/10' : 'bg-muted/30')}>
                          <TableCell colSpan={99} className="p-0">
                            <div className="p-4 border-t border-l-4 border-l-blue-500 overflow-x-auto">
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {strataConfig[m.measureId].strataLabel}
                              </p>
                              {(() => {
                                const cahps = r.result as CahpsScoreResult;
                                if (!Array.isArray(cahps.ssmResults) || cahps.ssmResults.length === 0) {
                                  return <p className="text-sm text-muted-foreground">No SSM entries yet — enter them in Configuration → CAHPS Entry.</p>;
                                }
                                const ssmDefs = logic ? logic.getCahpsSsms(programYear) : [];
                                return (
                                  <>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="text-left">Summary Survey Measure</TableHead>
                                        <TableHead className="text-center">n</TableHead>
                                        <TableHead className="text-center">Rate</TableHead>
                                        <TableHead className="text-center">Decile</TableHead>
                                        <TableHead className="text-center">Points</TableHead>
                                        <TableHead className="text-center">
                                          <CellTip on={tooltips} tip="Top-box points to the next decile floor — green = a whole decile within reach of a small survey-experience gain; amber = below the entire benchmark distribution (pinned at the 1.0 minimum until it clears the decile-1 floor)">
                                            <span>Next Decile</span>
                                          </CellTip>
                                        </TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {cahps.ssmResults.map(s => {
                                        const def = ssmDefs.find(x => x.ssmId === s.ssmId);
                                        return (
                                          <TableRow key={s.ssmId}>
                                            <TableCell className="text-left">{s.title}</TableCell>
                                            <TableCell className="text-center tabular-nums">{s.n}</TableCell>
                                            <TableCell className="text-center tabular-nums">{s.ratePct.toFixed(1)}%</TableCell>
                                            <TableCell className="text-center tabular-nums">{s.decile ?? '—'}</TableCell>
                                            <TableCell className="text-center tabular-nums">
                                              {s.scored && s.points != null ? s.points.toFixed(1) : <span title="n < 20 — dropped from the CAHPS average">dropped</span>}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              {s.scored && def ? <NextDecileChip ratePct={s.ratePct} row={def.row} /> : '—'}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                  <p className="mt-2 text-xs text-muted-foreground">{CAHPS_PROJECTION_CAVEAT}</p>
                                  </>
                                );
                              })()}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>

            {/* Confirm bar */}
            <div className="flex items-center justify-between gap-2 border-t p-3">
              <p className="text-xs text-muted-foreground">
                Enter clinical data in the measure sheet (click a measure ID). 321 and 479/484 are entered in{' '}
                <span className="font-medium">Configuration</span>. Changes are a what-if until confirmed.
              </p>
              <div className="flex gap-2">
                {dirty && <Button variant="ghost" size="sm" onClick={() => setDraft({})}>Discard</Button>}
                <Button size="sm" disabled={!dirty || saving || isCurrentYearLocked}
                  className={cn(dirty && 'bg-amber-600 hover:bg-amber-700')}
                  onClick={handleConfirm}>
                  {saving ? 'Saving…' : `Confirm Updates & Recalculate${dirty ? ` (${Object.keys(draft).length} pending)` : ''}`}
                </Button>
              </div>
            </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Manage Views — the SAME TableViewsManager component MIPS Gap Management
          uses (owner 2026-07-20): per-column visibility, reordering, rename/
          duplicate/delete, reset to system default. */}
      <TableViewsManager<QpsColKey>
        open={manageViewsOpen}
        onOpenChange={setManageViewsOpen}
        presets={presets}
        activePresetId={activePresetId}
        allColumns={QPS_COLUMNS}
        systemDefault={DEFAULT_SETTINGS}
        onCommit={(next, activeId) => {
          const nextUser = next.filter(p => !p.builtin);
          setUserViews(nextUser);
          const nextActive = activeId ?? activePresetId;
          persistViews(nextUser, nextActive);
          if (activeId) {
            setActivePresetId(activeId);
            const p = next.find(x => x.id === activeId);
            if (p) setColSettings(p.settings);
          } else {
            const cur = next.find(x => x.id === activePresetId);
            if (cur) setColSettings(cur.settings);
          }
        }}
      />

      {/* Chart Abstraction export modal — the SAME module MIPS uses, at ACO scope */}
      {selectedAco.shadow_practice_id && (
        <ChartAbstractionExportModal
          open={abstractionModalOpen}
          onOpenChange={setAbstractionModalOpen}
          practiceId={String(selectedAco.shadow_practice_id)}
          practiceName={selectedAco.name}
          measures={(score?.measures ?? []).map(res => {
            const m = measureSet.measures.find(x => x.measureId === res.measureId);
            const row = rowFor(res.measureId);
            return {
              id: res.measureId,
              title: m?.name ?? res.measureId,
              den: (row.eligible_population ?? 0) - (row.denominator_exclusions ?? 0),
              met: row.performance_met ?? 0,
              not: row.performance_not_met ?? 0,
              ex: row.denominator_exclusions ?? 0,
              exc: row.denominator_exceptions ?? 0,
            };
          })}
        />
      )}

      {/* The measure sheet — THE entry surface (D4) */}
      <AppMeasureSheet
        open={!!sheetId}
        onOpenChange={(open) => !open && setSheetId(null)}
        measure={sheetRow?.measure ?? null}
        row={sheetRow?.row ?? null}
        result={sheetRow?.result ?? null}
        cell={sheetRow?.cell ?? null}
        deciles={sheetRow ? decilesForCell(sheetRow.cell) : []}
        year={programYear}
        isClinical={!!sheetRow?.isClinical}
        locked={isCurrentYearLocked || !!ctOverride || sheetRow?.row.payload?.mode === 'calc'}
        onPatch={patch}
      />
    </div>
  );
}
